import { useEffect, useState } from "react";
import axios from "axios";
import { env } from "../constants/env";
import { db } from "../utils/offlineDB";
import { toast } from "react-toastify";
import { isFormEmpty } from "../utils/functions";
import { useAuth } from "../context/AuthContext";

export const useQuestionnaire = (formType, initialState) => {
  const { user } = useAuth();
  const labId = user?.labId;
  const userRole = user?.role;

  const [formData, setFormData] = useState(initialState);
  const [editMode, setEditMode] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const isReadOnly = userRole !== "LABORATORY" || !editMode;

  // Load from Dexie
  const loadLocal = async () => {
    return db.questionnaires.get({ labId, formType });
  };

  const saveLocal = async (data, isOnline = false) => {
    await db.questionnaires.put({
      labId,
      formType,
      ...data,
      timestamp: Date.now(),
      offline: !isOnline && isOffline,
    });
  };

  const fetchQuestionnaire = async () => {
    setIsFetching(true);

    const cached = await loadLocal();

    // OFFLINE MODE
    if (isOffline) {
      if (cached) {
        setFormData({ ...initialState, ...cached.answers });
        setEditMode(isFormEmpty(cached.answers));
      } else {
        setFormData(initialState);
        setEditMode(true);
      }
      setIsFetching(false);
      return;
    }

    // ONLINE MODE
    try {
      const res = await axios.get(
        `${env.kataApiUrl}/api/questionnaire/${labId}/${formType}`
      );

      const serverData = res.data;
      const serverAnswers = serverData?.answers || {};

      // Conflict: local edits newer than server?
      if (cached?.offline) {
        const serverTime = new Date(serverData.updatedAt || 0);
        const localTime = new Date(cached.timestamp || 0);

        if (localTime > serverTime) {
          await axios.post(
            `${env.kataApiUrl}/api/questionnaire/${formType}`,
            cached
          );

          toast.warn("Offline edits synced to server");
          await saveLocal(cached, true);

          setFormData({ ...initialState, ...cached.answers });
          return;
        }
      }

      // Normal case
      setFormData({ ...initialState, ...serverAnswers });
      await saveLocal(serverData, true);
      setEditMode(isFormEmpty(serverAnswers));
    } catch (error) {
      if (cached) {
        setFormData({ ...initialState, ...cached.answers });
        setEditMode(isFormEmpty(cached.answers));
        toast.warn("Server unreachable. Loaded offline version.");
      } else {
        toast.error("Error loading form");
        setFormData(initialState);
        setEditMode(true);
      }
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (labId) fetchQuestionnaire();
  }, [labId]);

  const handleChange = (field, value) => {
    if (!isReadOnly) {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleDone = async () => {
    setLoading(true);

    const payload = {
      labId,
      formType,
      answers: formData,
      timestamp: Date.now(),
    };

    try {
      if (navigator.onLine) {
        await axios.post(
          `${env.kataApiUrl}/api/questionnaire/${formType}`,
          payload
        );
        toast.success("Submitted!");

        await saveLocal(payload, true);
        setEditMode(false);
      } else {
        await saveLocal(payload);
        toast.info("Saved offline, will sync later");
        setEditMode(false);
      }
    } catch {
      toast.error("Submission failed");
      await saveLocal(payload);
    } finally {
      setLoading(false);
      fetchQuestionnaire();
    }
  };

  return {
    formData,
    setFormData,
    editMode,
    setEditMode,
    isReadOnly,
    isFetching,
    loading,
    handleChange,
    handleDone,
    fetchQuestionnaire,
    isOffline,
  };
};